import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pointsAPI } from '@/api/points';
import { getAuthToken, getPortalType, getUserData } from '@/utils/auth';

const PointsContext = createContext(null);

export const usePoints = () => {
  const context = useContext(PointsContext);
  if (!context) {
    throw new Error('usePoints must be used within a PointsProvider');
  }
  return context;
};

export const PointsProvider = ({ children }) => {
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const hasCheckedDailyLoginRef = useRef(false); // Track if we've checked today

  /**
   * Fetch current student's points from backend API
   * Only fetches for students - returns null for other user types
   */
  const refreshPoints = useCallback(async () => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    
    if (!token) {
      // Not authenticated - clear points
      setPoints(null);
      return null;
    }

    // Only fetch points for students
    const userData = getUserData(portal);
    if (!userData || userData.user_type !== 'student') {
      // Not a student - clear points and don't make API call
      setPoints(null);
      return null;
    }

    // Only fetch if portal is student
    if (portal !== 'student') {
      setPoints(null);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[PointsContext] Fetching points from API...');
      const data = await pointsAPI.getPoints();
      console.log('[PointsContext] Points fetched:', data);
      setPoints(data);
      return data;
    } catch (err) {
      console.error('[PointsContext] Error fetching points:', err);
      setError(err.message || 'Failed to fetch points');
      // Don't clear points on error - keep existing data
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Award points for an activity (and refresh points)
   * @param {Object} data - { amount_xp, amount_gems, reason, related_object_type, related_object_id }
   * @returns {Promise<Object>} Award result
   */
  const awardPoints = useCallback(async (data) => {
    try {
      const result = await pointsAPI.awardPoints(data);
      // Refresh points after awarding
      await refreshPoints();
      return result;
    } catch (err) {
      console.error('Error awarding points:', err);
      throw err;
    }
  }, [refreshPoints]);

  /**
   * Claim daily login bonus
   */
  const claimDailyLogin = useCallback(async () => {
    try {
      const result = await pointsAPI.claimDailyLogin();
      // Refresh points after claiming
      await refreshPoints();
      return result;
    } catch (err) {
      // Silently handle "already claimed" errors
      if (err.message?.includes('already claimed') || err.message?.includes('400')) {
        console.log('[PointsContext] Daily login already claimed today');
        return { claimed: false };
      }
      console.error('Error claiming daily login:', err);
      throw err;
    }
  }, [refreshPoints]);

  /**
   * Check and claim daily login bonus automatically (only for students)
   */
  const checkAndClaimDailyLogin = useCallback(async () => {
    const portal = getPortalType(window.location.pathname || '');
    const token = getAuthToken(portal);
    
    if (!token) {
      return;
    }

    // Only claim for students
    const userData = getUserData(portal);
    if (!userData || userData.user_type !== 'student') {
      return;
    }

    // Check if we've already checked today (prevent multiple calls)
    const today = new Date().toDateString();
    const lastCheckKey = `daily_login_check_${portal}_${today}`;
    if (hasCheckedDailyLoginRef.current || localStorage.getItem(lastCheckKey)) {
      return;
    }

    try {
      // Mark as checked (even if it fails, don't retry today)
      hasCheckedDailyLoginRef.current = true;
      localStorage.setItem(lastCheckKey, 'true');

      // Claim daily login bonus
      const result = await claimDailyLogin();
      
      if (result?.claimed) {
        console.log('[PointsContext] Daily login bonus claimed:', result.xp_awarded, 'XP');
      }
    } catch (err) {
      // Silently fail - don't show error to user
      console.log('[PointsContext] Could not claim daily login bonus:', err.message);
    }
  }, [claimDailyLogin]);

  /**
   * Buy heart refill with gems
   */
  const buyHeartRefill = useCallback(async () => {
    try {
      const result = await pointsAPI.buyHeartRefill();
      // Refresh points after purchase
      await refreshPoints();
      return result;
    } catch (err) {
      console.error('Error buying heart refill:', err);
      throw err;
    }
  }, [refreshPoints]);

  // Fetch points on mount and when route changes (to ensure fresh data)
  // Only for students
  useEffect(() => {
    const portal = getPortalType(window.location.pathname || '');
    const userData = getUserData(portal);
    
    // Only fetch points if user is a student
    if (portal !== 'student' || !userData || userData.user_type !== 'student') {
      setPoints(null);
      return;
    }

    const fetchAndClaim = async () => {
      const data = await refreshPoints();
      
      // After points are loaded, check and claim daily login bonus
      // Only do this once per day, and only for students
      if (data) {
        // Small delay to ensure points are fully loaded
        setTimeout(() => {
          checkAndClaimDailyLogin();
        }, 500);
      }
    };

    fetchAndClaim();
  }, [refreshPoints, checkAndClaimDailyLogin, location.pathname]);

  // Reset daily login check on new day
  useEffect(() => {
    const today = new Date().toDateString();
    const portal = getPortalType(window.location.pathname || '');
    const lastCheckKey = `daily_login_check_${portal}_${today}`;
    
    // Check if we have a check from a different day
    // Clean up old keys (keys with different dates)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`daily_login_check_${portal}_`) && key !== lastCheckKey) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // If we don't have today's check, reset the ref
    if (!localStorage.getItem(lastCheckKey)) {
      hasCheckedDailyLoginRef.current = false;
    }
  }, [location.pathname]);

  const value = {
    points,
    loading,
    error,
    refreshPoints,
    awardPoints,
    claimDailyLogin,
    buyHeartRefill,
  };

  return (
    <PointsContext.Provider value={value}>
      {children}
    </PointsContext.Provider>
  );
};
